/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

import { LayoutPatternSchema, LayoutPlanSchema } from './layout.js';

/**
 * A run is the assistant's work on one request: it plans, waits for approval, then builds. `superseded` is a plan the
 * user replaced by sending a new message instead of answering it.
 */
export const RunStatusSchema = z.enum([
  'planning',
  'awaiting_approval',
  'implementing',
  'done',
  'failed',
  'rejected',
  'superseded',
]);
export type RunStatus = z.infer<typeof RunStatusSchema>;

/** `device_error` is a run the cloud started itself because a device reported an error. */
export const RunSourceSchema = z.enum(['user', 'device_error']);
export type RunSource = z.infer<typeof RunSourceSchema>;

/**
 * How a criterion is shown to hold: `test` by a Go test the build runs against the virtual camera, `device` by what
 * the delivered app reports from the real device, `user` by the person looking at it.
 */
export const AcceptanceCheckSchema = z.enum(['test', 'device', 'user']);
export type AcceptanceCheck = z.infer<typeof AcceptanceCheckSchema>;

/**
 * The interface between the app's Go code and its page, fixed with the plan so the Go code, its tests and the page can be
 * written at the same time. Not shown to the user; null for plans from before it and for plans that need none.
 */
export const ContractSchema = z.object({
  go_api: z
    .string()
    .min(1)
    .max(8000)
    .describe(
      'Go declarations exactly as they will be written: the exported service methods the page calls (receiver, name, arguments, results) and every struct the page receives or sends, with json tags. A one-line comment per method on what it does and its errors.'
    ),
  element_ids: z
    .array(z.string().min(1).max(200))
    .max(60)
    .describe('Every control and every place that shows an outcome, as "id: what it is" (the check on the device uses them).'),
  probe: z
    .array(z.string().min(1).max(200))
    .max(20)
    .default([])
    .describe('Names the Go code exposes with probe.Expose, as "name: what it holds".'),
});
export type Contract = z.infer<typeof ContractSchema>;

/** One condition that must hold for the request to count as done; the plan lists them and the loop checks them. */
export const AcceptanceCriterionSchema = z.object({
  text: z.string().min(1).max(300),
  check: AcceptanceCheckSchema,
});
export type AcceptanceCriterion = z.infer<typeof AcceptanceCriterionSchema>;

/**
 * What the planner says about the page, so the look can be agreed before code is written. `impact` decides whether
 * design options are drafted: `new` (a new app or a redesign) and `major` (new screens or panels) get them.
 */
export const UiBriefSchema = z.object({
  impact: z.enum(['new', 'major', 'minor', 'none']),
  screens: z
    .array(
      z.object({
        name: z.string().min(1).max(80),
        purpose: z.string().min(1).max(300),
        parts: z.array(z.string().min(1).max(160)).max(20),
      })
    )
    .max(6)
    .default([]),
  flows: z.array(z.string().min(1).max(300)).max(8).default([]),
  data: z.array(z.string().min(1).max(160)).max(20).default([]),
  directions: z.array(z.string().min(1).max(40)).max(3).default([]),
  /** Missing in plans made before layouts were planned. */
  layout: LayoutPlanSchema.optional(),
});
export type UiBrief = z.infer<typeof UiBriefSchema>;

/** One drafted design: a screen in the app's own components, rendered to a picture of HTML the user can look at. */
export const DesignOptionSchema = z.object({
  option_id: z.string().min(1).max(40),
  direction: z.string().min(1).max(40),
  title: z.string().max(80),
  concept: z.string().max(300),
  theme: z.string().max(40),
  /** The layout pattern it is drawn in; the chosen one becomes the project's. Missing in options from before. */
  pattern: LayoutPatternSchema.optional(),
  state: z.enum(['ready', 'failed']),
  error: z.string().max(500).nullable().default(null),
});
export type DesignOption = z.infer<typeof DesignOptionSchema>;

export const DesignPreviewResponseSchema = z.object({ option_id: z.string(), html: z.string() });
export type DesignPreviewResponse = z.infer<typeof DesignPreviewResponseSchema>;

export const DesignChoiceSchema = z.object({
  option_id: z.string().min(1).max(40),
  note: z.string().trim().max(500).optional(),
});
export type DesignChoice = z.infer<typeof DesignChoiceSchema>;

export const RunSchema = z.object({
  project_id: z.string().uuid(),
  run_id: z.string().min(1),
  status: RunStatusSchema,
  request: z.string(),
  source: RunSourceSchema.default('user'),
  /** For a `device_error` run, when the device logged the error that started it. */
  trigger_at: z.string().datetime().nullable().default(null),
  /** The device the finished app is sent to; null builds and verifies only. */
  device_id: z.string().uuid().nullable().default(null),
  plan: z.string().nullable().default(null),
  /** What the plan promised, in checkable form; empty until a plan is proposed, and for automatic fixes. */
  acceptance: z.array(AcceptanceCriterionSchema).default([]),
  /** The planner's description of the page; null for runs from before it, and for answers. */
  ui: UiBriefSchema.nullable().default(null),
  /** The interface the plan fixed between the Go code and the page; null when it fixed none. */
  contract: ContractSchema.nullable().default(null),
  /** The design options drafted for the plan; empty when the change does not touch the look. */
  design_options: z.array(DesignOptionSchema).default([]),
  /** The option the user approved the plan with; null when there were none or none was chosen. */
  design_choice: DesignChoiceSchema.nullable().default(null),
  /** The version this run built, once it has. */
  version: z.number().int().positive().nullable().default(null),
  error: z.string().nullable().default(null),
  /**
   * Numbers about the run: how long each stage took (plan_ms, implement_ms, build_ms, deploy_ms, error_to_applied_ms),
   * what it used (builds, reviews, input_tokens, output_tokens), whether it reached the device (applied), and how often
   * each gate turned it back (fail_review, fail_go_test, fail_build, fail_deploy, fail_runtime, fail_assistant, ...).
   */
  metrics: z.record(z.string(), z.number()).default({}),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Run = z.infer<typeof RunSchema>;

export const ChatEventTypeSchema = z.enum(['user', 'plan', 'step', 'assistant', 'status', 'error']);
export type ChatEventType = z.infer<typeof ChatEventTypeSchema>;

export const ChatEventSchema = z.object({
  project_id: z.string().uuid(),
  /** Increases by one per event in a project; a client asks for what came after the last one it has. */
  seq: z.number().int().positive(),
  run_id: z.string().min(1),
  type: ChatEventTypeSchema,
  text: z.string(),
  version: z.number().int().positive().nullable().default(null),
  created_at: z.string().datetime(),
});
export type ChatEvent = z.infer<typeof ChatEventSchema>;

export const SendMessageRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(4000),
    device_id: z.string().uuid().optional(),
  })
  .strict();
export type SendMessageRequest = z.input<typeof SendMessageRequestSchema>;

export const DecideRunRequestSchema = z
  .object({ approved: z.boolean(), design: DesignChoiceSchema.optional() })
  .strict();
export type DecideRunRequest = z.infer<typeof DecideRunRequestSchema>;

export const ListChatResponseSchema = z.object({
  events: z.array(ChatEventSchema),
  /** The newest run, which is the one that may need an answer. */
  run: RunSchema.nullable(),
});
export type ListChatResponse = z.infer<typeof ListChatResponseSchema>;
